import { useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { v4 as uuid } from 'uuid';

import { useI18n, useLogger, useSelector } from '~Hooks';
import { useStorage } from '~Components/Storage';
import { HOTEL_CORRECT_ROOM_CHARGE } from '~Queries/hotel';
import { HotelCorrectRoomCharge, HotelCorrectRoomChargeVariables } from '~Queries/__queries__';
import { hotelErrorOf } from '~Hooks/Payments/useHotelRoomPayment/hotel-room';
import { RefundProps } from '../types';

const CONFLICT_RETRIES = 5;
const CONFLICT_DELAY_MS = 3000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const RefundHotelRoom = ({
  transactionId,
  amount,
  isFullRefund,
  isTipRefund,
  items,
  reopen,
  onCompleted,
  onFail,
}: RefundProps) => {
  const { i18n } = useI18n();
  const logger = useLogger('refund:hotel-room');
  const correctionId = useRef(uuid());
  const staffId = useSelector((state) => state.staff?.id);
  const [storage] = useStorage();
  const [correct] = useMutation<HotelCorrectRoomCharge, HotelCorrectRoomChargeVariables>(
    HOTEL_CORRECT_ROOM_CHARGE,
    { fetchPolicy: 'no-cache' },
  );

  useEffect(() => {
    if (!transactionId) {
      onFail('Missing transactionId');
      return;
    }

    // A whole order sends nothing, so the hotel takes back everything, tip included.
    // A tip refund sends only the tip; an item refund only its lines.
    const lines =
      isFullRefund || isTipRefund
        ? undefined
        : (items ?? []).map((item) => ({
          categoryId: item.categoryId || 'uncategorized',
          vatRate: item.vatRate,
          amount: Math.round(item.amount - item.discount),
        }));
    const variables: HotelCorrectRoomChargeVariables = {
      input: {
        correctionId: correctionId.current,
        chargeId: transactionId,
        lines,
        tip: isTipRefund ? amount : undefined,
        reopen: reopen || undefined,
        staffId: staffId ?? undefined,
        deviceId: storage.taxComplianceDeviceId || undefined,
      },
    };

    const run = async () => {
      logger.info('Refunding hotel room charge', {
        chargeId: transactionId,
        isFullRefund,
        isTipRefund,
        lines,
        tip: variables.input.tip,
        reopen,
      });
      for (let attempt = 0; attempt <= CONFLICT_RETRIES; attempt += 1) {
        let error: string | null | undefined;
        try {
          const { data } = await correct({ variables });
          if (data?.hotelCorrectRoomCharge.success) {
            logger.info('Hotel room charge refunded', { chargeId: transactionId });
            onCompleted();
            return;
          }
          error = data?.hotelCorrectRoomCharge.error;
        } catch (err) {
          logger.error('Hotel room refund did not answer', {
            chargeId: transactionId,
            errorMessage: (err as Error).message,
          });
          onFail(i18n('HotelRoom.Unreachable'));
          return;
        }

        const { code, detail } = hotelErrorOf(error);
        logger.warn('Hotel room refund refused', { chargeId: transactionId, code, detail });
        switch (code) {
          case 'ALREADY_CORRECTED':
            // The hotel has already taken the charge back, e.g. a reopen whose refund in
            // Baemingo failed and is now tried again: go on and refund the order
            logger.info('Hotel room charge already corrected, refunding the order', {
              chargeId: transactionId,
              reopen,
            });
            onCompleted();
            return;
          case 'DUPLICATE':
            onCompleted();
            return;
          case 'CHECKED_OUT':
            onFail(i18n('HotelRoom.CheckedOut'));
            return;
          case 'NOT_SAME_DAY':
            onFail(i18n('HotelRoom.ReopenNotSameDay'));
            return;
          case 'CONFLICT':
            if (attempt < CONFLICT_RETRIES) {
              await wait(CONFLICT_DELAY_MS);
              continue;
            }
            onFail(i18n('HotelRoom.Unreachable'));
            return;
          case 'NOT_FOUND':
            onFail(i18n('HotelRoom.RefundNotFound'));
            return;
          case 'INVALID_INPUT':
            onFail(i18n('HotelRoom.RefundMismatch'));
            return;
          case 'PMS_ERROR':
            onFail(`${i18n('HotelRoom.RefundRefused')} ${detail}`);
            return;
          default:
            onFail(i18n('HotelRoom.Error'));
            return;
        }
      }
    };

    run();
  }, []);

  return null;
};

export default RefundHotelRoom;
